import { getCountries } from "libphonenumber-js/max";

const frequent = ["CZ", "SK", "AT", "DE", "PL", "GB", "BE"];
const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

export function companyCountries(locale: string) {
  const names = new Intl.DisplayNames([locale], { type: "region" });
  const label = (code: string) => names.of(code) ?? code;
  return [
    ...frequent,
    ...getCountries()
      .filter((code) => !frequent.includes(code))
      .sort((a, b) => label(a).localeCompare(label(b), locale)),
  ].map((code) => ({ code, label: label(code) }));
}

/** Keep the canonical select for form/draft data; the bounded combobox owns interaction. */
export function mountCountryPicker(
  select: HTMLSelectElement,
  locale: string,
): void {
  const parent = select.parentElement!;
  const input = document.createElement("input");
  input.type = "text";
  input.id = "company-country-picker";
  input.autocomplete = "off";
  input.required = select.required;
  select.required = false;
  input.role = "combobox";
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-controls", "company-country-options");
  input.setAttribute("aria-expanded", "false");
  const list = document.createElement("div");
  list.id = "company-country-options";
  list.role = "listbox";
  list.hidden = true;
  select.hidden = true;
  parent.classList.add("country-picker");
  if (parent instanceof HTMLLabelElement) parent.htmlFor = input.id;
  select.after(input, list);
  const values = Array.from(select.options).map((o) => ({
    code: o.value,
    label: o.textContent ?? o.value,
  }));
  const selectedLabel = () =>
    values.find((o) => o.code === select.value)?.label ?? "—";
  input.value = selectedLabel();
  const validate = () =>
    input.setCustomValidity(
      input.required && !select.value
        ? locale === "cs"
          ? "Vyberte zemi společnosti."
          : "Select a company country."
        : "",
    );
  validate();
  let matches = values;
  let active = -1;
  let stopPositioning = () => {};
  const close = () => {
    stopPositioning();
    list.hidden = true;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    input.value = selectedLabel();
  };
  const choose = (code: string) => {
    select.value = code;
    close();
    select.dispatchEvent(new Event("change", { bubbles: true }));
  };
  const position = () => {
    if (!input.isConnected) return close();
    const box = input.getBoundingClientRect();
    const parentBox = parent.getBoundingClientRect();
    const viewport = window.visualViewport;
    const top = viewport?.offsetTop ?? 0;
    const bottom = top + (viewport?.height ?? window.innerHeight);
    if (box.bottom <= top || box.top >= bottom) return close();
    const below = Math.max(0, bottom - box.bottom - 8);
    const above = Math.max(0, box.top - top - 8);
    const openAbove = below < 160 && above > below;
    list.style.maxHeight = `${Math.max(0, Math.min(260, (bottom - top) * 0.4, openAbove ? above : below))}px`;
    list.style.top = openAbove ? "auto" : `${box.bottom - parentBox.top}px`;
    list.style.bottom = openAbove ? `${parentBox.bottom - box.top}px` : "auto";
  };
  const startPositioning = () => {
    stopPositioning();
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!list.hidden) position();
      });
    };
    window.addEventListener("scroll", schedule, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    const observer = new MutationObserver(() => {
      if (!input.isConnected) close();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    stopPositioning = () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
      observer.disconnect();
      stopPositioning = () => {};
    };
    position();
    if (!list.hidden) schedule(); // Focus can scroll the anchor after its synchronous event.
  };
  const render = (query: string) => {
    const q = normalize(query.trim());
    matches = values.filter((o) =>
      normalize(o.label + " " + o.code).includes(q),
    );
    active = -1;
    input.removeAttribute("aria-activedescendant");
    list.replaceChildren();
    matches.forEach((option, index) => {
      const row = document.createElement("div");
      row.role = "option";
      row.id = `company-country-${index}`;
      row.textContent = option.label;
      row.setAttribute("aria-selected", String(select.value === option.code));
      row.addEventListener("mousedown", (e) => e.preventDefault());
      row.addEventListener("click", () => choose(option.code));
      list.append(row);
    });
    if (!matches.length) {
      const empty = document.createElement("div");
      empty.role = "status";
      empty.textContent =
        locale === "cs" ? "Žádná odpovídající země" : "No matching country";
      list.append(empty);
    }
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
    startPositioning();
  };
  select.addEventListener("change", () => {
    input.value = selectedLabel();
    validate();
  });
  input.addEventListener("focus", () => {
    window.scrollTo({ top: window.scrollY, behavior: "instant" });
    input.scrollIntoView({ block: "nearest", behavior: "instant" });
    input.select();
    render("");
  });
  input.addEventListener("click", () => {
    if (list.hidden) render("");
  });
  input.addEventListener("input", () => render(input.value));
  input.addEventListener("blur", close);
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (list.hidden) render("");
      if (!matches.length) return;
      active =
        active < 0
          ? event.key === "ArrowDown"
            ? 0
            : matches.length - 1
          : (active + (event.key === "ArrowDown" ? 1 : -1) + matches.length) %
            matches.length;
      Array.from(list.children).forEach((row, i) =>
        row.setAttribute("aria-selected", String(i === active)),
      );
      const row = list.children[active] as HTMLElement;
      input.setAttribute("aria-activedescendant", row.id);
      row.scrollIntoView({ block: "nearest", behavior: "instant" });
    }
    if (event.key === "Enter" && !list.hidden) {
      event.preventDefault();
      const option =
        matches[active] ?? (matches.length === 1 ? matches[0] : undefined);
      if (option) choose(option.code);
    }
  });
}
